import { useAccountSettingsStore } from '@/stores/AccountSettingsStore'
import { storeToRefs } from 'pinia'
import { computed, inject } from 'vue'
import {
  CreateCustomerPortalSessionError,
  DuplicateSubscriptionError
} from '../config/accountSettings.enums'
import { useModals } from '@/composables/useModal/useModal'
import { useI18n } from 'vue-i18n'
import type { TrackingTrackFunction } from '@/global'
import { useRoute, useRouter } from 'vue-router'
import { br } from '@/plugins/trackerPlugin'
import { useCommonStore } from '@/stores/CommonStore'
import type { CreateCustomerPortalSessionRequest } from '../config/accountSettings.types'

export const useAccountSettingsPlanDetails = () => {
  const commonStore = useCommonStore()
  const { loading, fullUserInfo } = storeToRefs(commonStore)

  const accountSettingsStore = useAccountSettingsStore()
  const {
    getActiveSubscription,
    subscriptionInfo,
    getHasSubscriptionIssue,
    isAndroidSubscription,
    isAppleSubscription,
    isPromotional
  } = storeToRefs(accountSettingsStore)
  const { setPaywallPreviousPage, createCustomerPortalSession } =
    accountSettingsStore

  const { t } = useI18n()

  const route = useRoute()
  const router = useRouter()

  const { createSlot, generateModal, HTMLtoComponent } = useModals()

  const track = inject<TrackingTrackFunction>(
    '$trackingTrack'
  ) as TrackingTrackFunction

  const multipleSubscriptionsThroughDescription = computed(() => {
    if (!getActiveSubscription.value) {
      return []
    }

    switch (subscriptionInfo.value.duplicateSubscriptionError) {
      case DuplicateSubscriptionError.AppleApple:
        return ['Apple App Store']
      case DuplicateSubscriptionError.BraintreeApple:
        return ['TalkingParents.com', 'Apple App Store']
      case DuplicateSubscriptionError.BraintreeBraintree:
        return ['TalkingParents.com']
      default:
        return []
    }
  })

  const routeToPaywall = async () => {
    await setPaywallPreviousPage({ name: route.name?.toString() })
    // bloomreach
    if (!subscriptionInfo.value.highestTier) {
      track(br.eventTypes.appAction, {
        feature: br.appActionFeature.settings,
        name: br.appActionEventNames.CTAUpgrade
      })
    }
    router.push({ name: 'accountTypeStep1' })
  }

  const toggleSubscriptionIssueModal = () => {
    const _subscriptionsText = `
    <div class="stack size-1">
      <span>${t('accountSettings.planDetails.multiSubModal.subList')}</span>
      <ul>
          ${multipleSubscriptionsThroughDescription.value
            .map(
              (item: string) =>
                `<li><span class="brand underline">${item}</span></li>`
            )
            .join('')}
      </ul>
      <span>${t('accountSettings.planDetails.multiSubModal.cancelText')}</span>
      <span class="text-2 font-size-0">${t('accountSettings.planDetails.multiSubModal.tpSub')}</span>
    </div>`

    generateModal({
      slot: createSlot('content', HTMLtoComponent(_subscriptionsText)),
      default: {
        headerText: t('accountSettings.planDetails.multiSubModal.title')
      },
      config: { showFooter: false }
    })
  }

  const toggleAppleSubscriptionModal = () => {
    generateModal({
      default: {
        headerText: t('accountSettings.planDetails.appleSubModal.title'),
        contentText: t('accountSettings.planDetails.appleSubModal.content'),
        footerButtonLabel: t('accountSettings.planDetails.appleSubModal.cta')
      },
      config: { closeOnConfirm: true },
      callback: { confirmFn: handleMobileModalButtonClick }
    })
  }

  function toggleAndroidSubscriptionModal() {
    generateModal({
      default: {
        headerText: t('accountSettings.planDetails.androidSubModal.title'),
        contentText: t('accountSettings.planDetails.androidSubModal.content'),
        footerButtonLabel: t('accountSettings.planDetails.androidSubModal.cta')
      },
      config: { closeOnConfirm: true },
      callback: { confirmFn: handleMobileModalButtonClick }
    })
  }

  function togglePromotionalSubscriptionModal() {
    generateModal({
      default: {
        headerText: t(
          'accountSettings.planDetails.complimentaryPlanModal.title'
        ),
        contentText: t(
          'accountSettings.planDetails.complimentaryPlanModal.content'
        ),
        footerButtonLabel: t(
          'accountSettings.planDetails.complimentaryPlanModal.cta'
        )
      },
      config: { closeOnConfirm: true },
      callback: {
        confirmFn: () =>
          (window.location.href = 'mailto:support@talkingparents.com')
      }
    })
  }

  function toggleSubscriptionMigrationModal() {
    generateModal({
      default: {
        headerText: t(
          'accountSettings.planDetails.subscriptionMigrationModal.title'
        ),
        contentText: t(
          'accountSettings.planDetails.subscriptionMigrationModal.content'
        ),
        footerButtonLabel: t(
          'accountSettings.planDetails.subscriptionMigrationModal.cta'
        )
      },
      config: { closeOnConfirm: true },
      callback: {
        confirmFn: () =>
          (window.location.href = 'mailto:support@talkingparents.com')
      }
    })
  }

  const handleMobileModalButtonClick = () => {
    const _userAgent = navigator.userAgent.toLowerCase()

    const _indexOfAndroid = _userAgent.indexOf('android')
    const _indexOfIPhone = _userAgent.indexOf('iphone')

    if (_indexOfAndroid > -1 && isAndroidSubscription.value) {
      window.location.href =
        'intent://play.google.com/store/account/subscriptions#Intent;scheme=https;package=com.android.vending;end'
    } else if (isAndroidSubscription.value) {
      window.location.href =
        'https://play.google.com/store/account/subscriptions'
    } else if (_indexOfIPhone > -1 && isAppleSubscription.value) {
      window.location.href = 'itms-apps://apps.apple.com/account/subscriptions'
    }
  }

  const canManageSubscription = computed(
    () =>
      (!getHasSubscriptionIssue.value ||
        subscriptionInfo.value.duplicateSubscriptionError ==
          DuplicateSubscriptionError.BraintreeApple) &&
      !loading.value
  )

  const manageSubscription = async () => {
    //stripe subscriptions: will always be in active array, never in suspended array, so manage plan button will show
    //BUT we disallow multiple stripe subscriptions, so we redirect back and show a modal
    //google/apple subscriptions: will be in suspended array if in retry state, and not in active array, so manage button will never show for those cases
    //the optionSelected function will show an error modal for suspended subs in these cases

    if (getHasSubscriptionIssue.value) {
      toggleSubscriptionIssueModal()
    } else if (isAppleSubscription.value) {
      toggleAppleSubscriptionModal()
    } else if (isAndroidSubscription.value) {
      toggleAndroidSubscriptionModal()
    } else if (isPromotional.value) {
      togglePromotionalSubscriptionModal()
    } else if (fullUserInfo.value.subscriptionMigrationInProgress) {
      toggleSubscriptionMigrationModal()
    } else if (canManageSubscription.value) {
      const payloadPortal: CreateCustomerPortalSessionRequest = {
        returnUrl: `${
          import.meta.env.VITE_APP_BASE_URL
        }accountSettings/planDetails`,
        priceId: '',
        planLevel: '',
        upgrade: false
      }

      const session = await createCustomerPortalSession(payloadPortal)

      if (session?.sessionUrl) {
        window.location.href = session.sessionUrl
      } else if (
        Number(session?.errorCode) ===
        CreateCustomerPortalSessionError.MigrationInProgress
      ) {
        toggleSubscriptionMigrationModal()
      }
    } else {
      // do nothing
    }
  }

  return {
    multipleSubscriptionsThroughDescription,
    toggleSubscriptionIssueModal,
    routeToPaywall,
    canManageSubscription,
    manageSubscription
  }
}
